import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Search } from "lucide-react";
import { Dropdown } from "react-bootstrap";
import DebouncedInput from "Components/Common/DebouncedInput";
import { useNavigate, useLocation } from "react-router-dom";

type TColumn = {
    key: string;
    label: string;
};
interface SearchActionPlansProps {
    urlParams?: URLSearchParams;
    columns: TColumn[];
    onSearch: (searchTerm: string, key: string) => void;
}

export default function SearchActionPlans({ columns, urlParams, onSearch }: SearchActionPlansProps) {
    const navigate = useNavigate();
    const location = useLocation();

    const [searchTerm, setSearchTerm] = useState<string>("");
    const [option, setOption] = useState<string>(columns[0].key);
    const [showDropdown, setShowDropdown] = useState<boolean>(false);
    const hasInteracted = useRef<boolean>(false);

    const getColumnLabel = (key: string) => {
        const column = columns.find((col) => col.key === key);
        return column ? column.label : "";
    };

    const handleSearch = (value: string) => {
        if (onSearch) {
            onSearch(value, option);
        }
        // TODO: clear the searchbar?
        setSearchTerm(value);
    };

    useEffect(() => {
        if (!hasInteracted.current) {
            hasInteracted.current = true;
            return;
        }

        if (searchTerm) {
            onSearch(searchTerm, option);

            urlParams?.set("query", searchTerm);
            urlParams?.set("key", option);
        } else {
            urlParams?.delete("query");
            urlParams?.delete("key");
        }

        navigate({
            pathname: location.pathname,
            search: urlParams?.toString(),
        });
    }, [searchTerm, option]);

    useEffect(() => {
        const search = urlParams?.get("query") || "";
        const key = urlParams?.get("key") || "";
        const isParamsPresent = search && key;

        // apply search on page load
        if (onSearch && isParamsPresent) {
            setSearchTerm(search);
            setOption(key);
            // Delay to avoid race condition with other filters
            setTimeout(() => {
                onSearch(search, key);
            }, 100);
        }
    }, []);

    return (
        <div className="searchbox d-flex align-items-center gap-2">
            <div className="trailing-icon">
                <Search />
            </div>
            <DebouncedInput value={searchTerm} onChange={(value) => handleSearch(value as string)} placeholder="Search by:" />
            <Dropdown show={showDropdown} onToggle={(isOpen) => setShowDropdown(isOpen)} className="filter-modifier__dropdown">
                <Dropdown.Toggle tabIndex={0} className="d-flex align-items-center gap-2">
                    <span>{option ? `${getColumnLabel(option)}` : "select a modifier"}</span>
                    <div className="trailing-icon">{showDropdown ? <ChevronUp size={20} /> : <ChevronDown size={20} />}</div>
                </Dropdown.Toggle>

                <Dropdown.Menu>
                    {columns.map(({ key, label }) => (
                        <Dropdown.Item
                            key={key}
                            onClick={() => {
                                setOption(key), onSearch(searchTerm, key);
                            }}
                        >
                            {label}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
}
